import { createSlice } from "@reduxjs/toolkit";
import {
  Addition,
  CasterProcessData,
  Charge,
  EAFProcessData,
  HeatData,
  HeatReportDelay,
  Heating,
  Probe,
  StrandData,
  LMFProcessData,
  TorchesProcessData,
} from "../models/HeatReportModels";
import { ChemistrySample } from "../models/ElementData";
import { StrandTimeSeriesData } from "../models/StrandSpeed";
import {
  CarbonInjection,
  ProcessPhase,
  Slag,
} from "@/pages/eaf/data-entry/models/eaf.models";
import { TrendingDataByTagId } from "@/pages/caster/data-entry/models/caster.models";
import { Torch } from "@/pages/caster/torches-data-entry/models/torches.models";

const HeatReportInitialState: {
  isDataLoading: boolean;
  heat: HeatData;
  eafProcessData: EAFProcessData;
  casProcessData: CasterProcessData;
  lmfProcessData: LMFProcessData | null;
  torchesProcessData: TorchesProcessData | null;
  elementData: ChemistrySample | null;
  strandData: StrandData[];
  probes: Probe[];
  charges: Charge[];
  additions: Addition[];
  heatings: Heating[];
  delays: HeatReportDelay[];
  phases: ProcessPhase[];
  injections: CarbonInjection[];
  slags: Slag[];
  strandSpeeds: TrendingDataByTagId[];
  moldLevels: TrendingDataByTagId[];
  torches: Torch[];
  groupedLength: StrandTimeSeriesData[];
  billetsYard: TrendingDataByTagId[];
  treatments: Pick<HeatData, "ProcessId" | "TreatmentNumber">[];
} = {
  isDataLoading: false,
  heat: {
    HeatName: "",
    HeatId: 0,
    ProcessId: null,
    Grade: "",
    LadleName: null,
    HeatsOnLadle: null,
    TapDate: null,
    TapShift: null,
    TapCrew: null,
    OpenDate: null,
    OpenShift: null,
    OpenCrew: null,
    FinalStatus: null,
    TreatmentNumber: null,
    SteelWeight: null,
    EAFProcessId: null,
    LMFProcessId: null,
    CASProcessId: null,
    NextHeatId: null,
    PreviousHeatId: null,
    NextHeatName: null,
    PreviousHeatName: null,
  },
  eafProcessData: {
    ProcessId: 0,
    Start: new Date(),
    End: null,
    StartTap: null,
    StartHeat: null,
    TapToTap: 0,
    PowerOn: 0,
    PowerOff: 0,
    SteelWeight: null,
    Energy: 0,
    KWHPerTon: null,
    MWH: null,
    Foreman: null,
    Operator: null,
    Lime: 0,
    Heel: null,
    InjectedCarbon: null,
    InjectedO2: null,
    Electrode1: false,
    Electrode2: false,
    Electrode3: false,
    IsNonFreeOpen: null,
    Comments: null,
    DelayTime: null,
    PrevHeatId: null,
    NextHeatId: null,
    PrevHeatName: null,
    NextHeatName: null,
    AssetId: 0,
    AssetName: "",
  },
  lmfProcessData: {
    ProcessId: 0,
    TreatmentNumber: 0,
    LadleCar: null,
    Start: new Date(),
    End: null,
    Arrive: null,
    Ship: null,
    ProcessTime: null,
    TapToTap: 0,
    PowerOn: 0,
    PowerOff: 0,
    KWHPerTon: null,
    Foreman: null,
    OperatorName: null,
    ElectrodeConsumption: null,
    ArcStability: null,
    Comments: null,
    DelayTime: null,
    PrevHeatId: null,
    NextHeatId: null,
    PrevHeatName: null,
    NextHeatName: null,
    AssetId: 0,
    AssetName: "",
    CarbonWeight: null,
    CaC2Weight: null,
    PlugInsideTime: null,
    PlugOutsideTime: null,
    PlugInsideVol: null,
    PlugOutsideVol: null,
    EmergencyLanceUsed: null,
    EmergencyLanceTime: null,
    EmergencyLanceTimeFormatted: null,
    EnergyConsumption: null,
    Electrode1: null,
    Electrode2: null,
    Electrode3: null,
    Argon: null,
    Nitrogen: null,
    LastProbeTemperature: null,
    LastProbeTime: null,
    OxigenConsumption: null,
    IsArriveManual: null,
    IsShipManual: null,
    EAFSlaggedInLadle: false,
  },
  casProcessData: {
    ProcessId: 0,
    AssetId: 0,
    AssetName: "",
    IsFreeOpen: false,
    Start: new Date(),
    End: null,
    LadleOpen: null,
    LadleClose: null,
    Duration: 0,
    NozzleSize: null,
    HeatsOnNozzle: null,
    TundishName: null,
    HeatsOnTundish: null,
    TundishShroudName: null,
    HeatsOnTundishShroud: null,
    Foreman: null,
    Operator: null,
    PourbackWeight: null,
    CastTons: null,
    ScrapTons: null,
    IsManualOpen: false,
    IsLadleShrouded: false,
    Comments: null,
  },
  torchesProcessData: {
    TorchesOperatorForemanId: null,
    TorchesForemanFillInName: null,
    Foreman: null,
    Operator: null,
    TorchesComments: null,
  },
  elementData: {
    HeatId: 0,
    AssetId: 0,
    ChemistrySampleId: null,
    Reception: null,
    Technician: null,
    SampleTypeName: null,
    SampleSequence: null,
    ElementId: null,
    ElementName: null,
    Decimals: null,
    Order: null,
    Value: null,
    MinValue: null,
    AimValue: null,
    MaxValue: null,
    SampleTypeId: null,
    Status: null,
  },
  probes: [],
  charges: [],
  additions: [],
  heatings: [],
  delays: [],
  phases: [],
  injections: [],
  slags: [],
  strandSpeeds: [],
  moldLevels: [],
  strandData: [],
  torches: [],
  groupedLength: [],
  billetsYard: [],
  treatments: [],
};

export const HeatReportReducer = createSlice({
  name: "__",
  initialState: HeatReportInitialState,
  reducers: {
    setIsDataLoading(state, { payload }) {
      state.isDataLoading = payload;
    },
    setHeat(state, { payload }) {
      state.heat = payload;
    },
    setEAFProcessData(state, { payload }) {
      state.eafProcessData = payload;
    },
    setCASProcessData(state, { payload }) {
      state.casProcessData = payload;
    },
    setTorchesData(state, { payload }) {
      state.torchesProcessData = payload;
    },
    setLMFProcessData(state, { payload }) {
      state.lmfProcessData = payload;
    },
    setElementData(state, { payload }) {
      state.elementData = payload;
    },
    setStrandData(state, { payload }) {
      state.strandData = payload;
    },
    setProbes(state, { payload }) {
      state.probes = payload;
    },
    setCharges(state, { payload }) {
      state.charges = payload;
    },
    setAdditions(state, { payload }) {
      state.additions = payload;
    },
    setHeatings(state, { payload }) {
      state.heatings = payload;
    },
    setDelays(state, { payload }) {
      state.delays = payload;
    },
    setPhases(state, { payload }) {
      state.phases = payload;
    },
    setInjections(state, { payload }) {
      state.injections = payload;
    },
    setSlags(state, { payload }) {
      state.slags = payload;
    },
    setStrandSpeeds(state, { payload }) {
      state.strandSpeeds = payload;
    },
    setMoldLevels(state, { payload }) {
      state.moldLevels = payload;
    },
    setTorches(state, { payload }) {
      state.torches = payload;
    },
    setGroupedLength(state, { payload }) {
      state.groupedLength = payload;
    },
    setBilletsYard(state, { payload }) {
      state.billetsYard = payload;
    },
    setTreatments(state, { payload }) {
      state.treatments = payload;
    },
  },
});
